import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { IndianRupee, TrendingUp, Store, AlertCircle, Crown, Award, Store as StoreIcon, Calendar, ExternalLink, Zap, Copy, Check } from "lucide-react";

export default function DirectoryRevenuePage() {
  const [data, setData] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [linkCopied, setLinkCopied] = useState("");

  const load = () => Promise.all([
    api.get("/admin/directory-revenue").then((r) => setData(r.data)),
    api.get("/admin/businesses").then((r) => setBusinesses(r.data)),
  ]).finally(() => setLoading(false));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const markPaid = async (bid, plan, months = 1) => {
    setBusyId(bid);
    try {
      await api.post(`/admin/businesses/${bid}/upgrade`, { plan, months });
      toast.success(`✅ ${plan.toUpperCase()} plan · ${months} month activated!`);
      await load();
    } catch (e) { toast.error("Upgrade failed"); }
    finally { setBusyId(""); }
  };

  const genLink = async (bid, plan, months = 1) => {
    setBusyId(bid);
    try {
      const { data: res } = await api.post(`/admin/businesses/${bid}/payment-link`, { plan, months });
      navigator.clipboard.writeText(res.short_url);
      setLinkCopied(bid); setTimeout(() => setLinkCopied(""), 3000);
      toast.success(`💳 Payment link copied! WhatsApp पर share करो · ₹${res.amount}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Payment link generate नहीं हो सका"); }
    finally { setBusyId(""); }
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Loading…</div>;
  if (!data) return <div className="p-8 text-center text-red-600">Data load नहीं हो सकी</div>;

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="dir-revenue-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-black text-slate-500">Business Directory</div>
        <h1 className="font-devnagri-heading text-2xl md:text-3xl font-black text-[#0F172A]">Listing Revenue Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">आपकी hyperlocal directory से monthly recurring earnings</p>
      </div>

      {/* MRR + Yearly big cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 text-white p-6 shadow-xl">
          <div className="flex items-center gap-2 mb-1">
            <IndianRupee className="w-5 h-5"/>
            <div className="text-[10px] uppercase tracking-widest font-black text-emerald-100">Monthly Recurring Revenue</div>
          </div>
          <div className="font-black text-5xl tabular-nums" data-testid="mrr">₹{Number(data.mrr).toLocaleString("hi-IN")}</div>
          <div className="text-xs text-emerald-100 mt-2">प्रति माह guaranteed income · जब सारे active listings paid हों</div>
        </div>
        <div className="rounded-2xl bg-gradient-to-br from-amber-500 via-orange-600 to-rose-600 text-white p-6 shadow-xl">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-5 h-5"/>
            <div className="text-[10px] uppercase tracking-widest font-black text-amber-100">Yearly Potential</div>
          </div>
          <div className="font-black text-5xl tabular-nums">₹{Number(data.yearly_potential).toLocaleString("hi-IN")}</div>
          <div className="text-xs text-amber-100 mt-2">12 महीने × MRR — अगर सारे renewals होते हैं</div>
        </div>
      </div>

      {/* Plan breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { plan: "free", label: "Free", color: "slate", price: 0, icon: StoreIcon },
          { plan: "silver", label: "Silver ₹499", color: "slate", price: 499, icon: Award },
          { plan: "gold", label: "Gold ₹999", color: "amber", price: 999, icon: Crown },
          { plan: "total", label: "Active Total", color: "emerald", price: null, icon: Store },
        ].map((c) => {
          const count = c.plan === "total" ? data.active_businesses : data.plan_counts[c.plan];
          const rev = c.plan === "total" ? data.mrr : (data.revenue_by_plan[c.plan] || 0);
          const Icon = c.icon;
          return (
            <div key={c.plan} className="rounded-xl bg-white border-2 border-slate-100 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 text-${c.color}-500`}/>
                <div className="text-[10px] uppercase tracking-widest font-black text-slate-500">{c.label}</div>
              </div>
              <div className="font-black text-3xl text-[#0F172A] tabular-nums">{count}</div>
              {c.plan !== "total" && c.price > 0 && (
                <div className="text-xs text-emerald-700 font-bold mt-1">= ₹{Number(rev).toLocaleString("hi-IN")}/mo</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Expiring soon */}
      <div className="rounded-xl bg-white border-2 border-slate-100 overflow-hidden">
        <div className="px-4 py-3 bg-red-50 border-b-2 border-red-100 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600"/>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-black text-red-700">Expiring in 15 days</div>
            <div className="font-devnagri-heading text-lg font-black text-[#0F172A]">Renewal Follow-ups ({data.expiring_soon.length})</div>
          </div>
        </div>
        {data.expiring_soon.length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm">कोई renewal pending नहीं — सब कुछ up-to-date है ✅</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.expiring_soon.map((b) => (
              <li key={b.id} className="flex items-center gap-3 px-4 py-3">
                <div className="w-9 h-9 rounded-full bg-red-100 text-red-600 flex items-center justify-center"><Calendar className="w-4 h-4"/></div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-[#0F172A] truncate">{b.name}</div>
                  <div className="text-[11px] text-slate-500">Plan: <b className="uppercase">{b.plan}</b> · Expires: {new Date(b.expires_at).toLocaleDateString("hi-IN")}</div>
                </div>
                {b.phone && <a href={`tel:${b.phone}`} className="text-xs font-bold text-emerald-600 hover:underline">📞 {b.phone}</a>}
                <Link to={`/biz/${b.slug}`} target="_blank" className="text-slate-500 hover:text-[#DC2626]"><ExternalLink className="w-4 h-4"/></Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Info block */}
      <div className="rounded-xl bg-amber-50 border-2 border-amber-200 p-4">
        <div className="font-black text-sm text-amber-900 mb-2">💡 Revenue बढ़ाने के tips</div>
        <ul className="text-xs text-amber-800 space-y-1 list-disc list-inside">
          <li>हर business को WhatsApp पर Silver/Gold plan पेश करें — QR-based profile + reviews + WhatsApp CTA</li>
          <li>Free listings को 30 दिन बाद upgrade prompt भेजो</li>
          <li>Local shops में INDIAN-NEWS-21 poster चिपकाओ — "आपका free listing यहाँ ready है" QR के साथ</li>
          <li>Razorpay LIVE keys आने पर सारा payment ऑटो-collect होगा — अभी manually mark करें</li>
        </ul>
      </div>

      {/* Businesses table with per-row Sell Plan actions */}
      <div className="rounded-xl bg-white border-2 border-slate-100 overflow-hidden" data-testid="biz-sell-table">
        <div className="px-4 py-3 border-b-2 border-slate-100 bg-slate-50">
          <div className="text-[10px] uppercase tracking-widest font-black text-slate-500">Sell / Upgrade Plans</div>
          <div className="font-devnagri-heading text-lg font-black text-[#0F172A]">All Businesses ({businesses.length})</div>
        </div>
        {businesses.length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm">कोई business listing नहीं। पहले `/admin/businesses` से add करें (या Boss से बोलो)</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest font-black text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Business</th>
                  <th className="text-left px-4 py-2">Plan</th>
                  <th className="text-left px-4 py-2">Expires</th>
                  <th className="text-right px-4 py-2 whitespace-nowrap">💳 Silver ₹499</th>
                  <th className="text-right px-4 py-2 whitespace-nowrap">👑 Gold ₹999</th>
                  <th className="text-right px-4 py-2 whitespace-nowrap">🏷️ Stickers</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {businesses.map((b) => {
                  const busy = busyId === b.id;
                  return (
                    <tr key={b.id}>
                      <td className="px-4 py-2">
                        <div className="font-bold text-[#0F172A] truncate">{b.name}</div>
                        <div className="text-[11px] text-slate-500">{b.phone || "—"}</div>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-block text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${b.listing_plan === "gold" ? "bg-amber-500 text-white" : b.listing_plan === "silver" ? "bg-slate-500 text-white" : "bg-slate-200 text-slate-700"}`}>{b.listing_plan || "free"}</span>
                      </td>
                      <td className="px-4 py-2 text-[11px] text-slate-600">{b.listing_expires_at ? new Date(b.listing_expires_at).toLocaleDateString("hi-IN") : "—"}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <button onClick={() => genLink(b.id, "silver", 1)} disabled={busy} className="text-[10px] font-black bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1 rounded mr-1" data-testid={`link-silver-${b.id}`}>💳 Link</button>
                        <button onClick={() => markPaid(b.id, "silver", 1)} disabled={busy} className="text-[10px] font-black bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded" data-testid={`paid-silver-${b.id}`}>✓ Paid</button>
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <button onClick={() => genLink(b.id, "gold", 1)} disabled={busy} className="text-[10px] font-black bg-amber-100 hover:bg-amber-200 text-amber-800 px-2 py-1 rounded mr-1" data-testid={`link-gold-${b.id}`}>💳 Link</button>
                        <button onClick={() => markPaid(b.id, "gold", 1)} disabled={busy} className="text-[10px] font-black bg-amber-600 hover:bg-amber-700 text-white px-2 py-1 rounded" data-testid={`paid-gold-${b.id}`}>✓ Paid</button>
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <a href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/biz/${b.slug}/sticker-pack?fmt=pdf`} target="_blank" rel="noopener noreferrer" className="text-[10px] font-black bg-sky-600 hover:bg-sky-700 text-white px-2 py-1 rounded mr-1 inline-block" data-testid={`sticker-pdf-${b.id}`}>PDF</a>
                        <a href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/biz/${b.slug}/sticker-pack?fmt=jpg`} target="_blank" rel="noopener noreferrer" className="text-[10px] font-black bg-slate-600 hover:bg-slate-700 text-white px-2 py-1 rounded inline-block" data-testid={`sticker-jpg-${b.id}`}>JPG</a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-4 py-3 bg-emerald-50 border-t-2 border-emerald-100 text-xs text-emerald-900">
          <b>💳 Link</b> = Razorpay payment link generate + auto-copy होगा → WhatsApp पर customer को भेजो · Payment मिलते ही "<b>✓ Paid</b>" दबाकर plan instantly upgrade करें · Razorpay LIVE keys आने पर webhook से auto-upgrade होगा।
        </div>
      </div>
    </div>
  );
}
